import { Radio, WifiOff, Loader2, CloudUpload } from 'lucide-react';
import { cn } from '../lib/cn';
import type { EstadoConexion } from '../lib/ws';

/**
 * Badge de salud del canal en vivo (hito M1). El modo degradado es un ciudadano
 * de primera clase: se comunica con calma ("sin conexión en vivo"), no como una
 * falla vergonzante, porque la sala sigue funcionando por polling.
 */
interface IndicadorConexionProps {
  estado: EstadoConexion;
  /** Aportes esperando en la cola offline (opcional; muestra un chip aparte). */
  pendientes?: number;
  className?: string;
}

const mapa: Record<
  EstadoConexion,
  { texto: string; clase: string; icono: 'radio' | 'loader' | 'wifi' }
> = {
  en_vivo: { texto: 'En vivo', clase: 'text-cyan', icono: 'radio' },
  conectando: { texto: 'Conectando…', clase: 'text-content-muted', icono: 'loader' },
  degradado: { texto: 'Sin conexión en vivo', clase: 'text-warning', icono: 'wifi' },
};

export function IndicadorConexion({ estado, pendientes = 0, className }: IndicadorConexionProps) {
  const c = mapa[estado];

  return (
    <span className={cn('inline-flex items-center gap-3', className)}>
      <span
        className={cn('inline-flex items-center gap-1.5 font-sans text-sm font-semibold', c.clase)}
        role="status"
        aria-live="polite"
      >
        {c.icono === 'radio' && <Radio className="h-4 w-4 animate-pulse" aria-hidden="true" />}
        {c.icono === 'loader' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {c.icono === 'wifi' && <WifiOff className="h-4 w-4" aria-hidden="true" />}
        {c.texto}
      </span>

      {pendientes > 0 && (
        <span
          className="inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-warning"
          title="Aportes guardados que se reenviarán al reconectar"
        >
          <CloudUpload className="h-4 w-4" aria-hidden="true" />
          {pendientes} en cola
        </span>
      )}
    </span>
  );
}
